/**
 * The steps of one instance, in flow order, with WHO has each one.
 *
 * WHY THIS EXISTS RATHER THAN ONLY THE CANVAS
 * ------------------------------------------
 * The instance page already had `FlowViewer` — a ReactFlow pan/zoom graph. It answers "what is
 * the shape of this process", which an admin asks once. It cannot answer the question they
 * actually open this page for:
 *
 *     "Where is my expense claim, and who has it?"
 *
 * A graph makes that a hunt: find the highlighted node, then look somewhere else for the person,
 * because the canvas is fed only `flow` and `currentNodeId` and has never known about tasks. For
 * a 4-to-7-node approval flow — which is every flow this product ships — a vertical list answers
 * all of it at a glance with no panning: order, step, who, outcome, when.
 *
 * So the canvas is kept and this sits ABOVE it: the list is what you read, the canvas is there
 * when you want the branches.
 *
 * ROLE SEATS ARE THE REASON `eligible_approver_ids` IS HERE
 * --------------------------------------------------------
 * When a step resolves to a role rather than a person, `assigned_to` is NULL on purpose —
 * "whoever is on finance" means whoever gets there first. A page reading only `assigned_to`
 * therefore shows "Unassigned" on every role-based approval, which is exactly wrong: the seat may
 * have three people in it. Both are read, and the list says which case it is.
 */
import React, { useMemo } from 'react';
import {
  Avatar,
  Box,
  Chip,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle as DoneIcon,
  Cancel as RejectedIcon,
  HourglassEmpty as PendingIcon,
  RadioButtonUnchecked as UpcomingIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

export interface StepTask {
  id: string;
  node_id?: string | null;
  assigned_to?: string | null;
  eligible_approver_ids?: string[] | null;
  status: string;
  outcome?: string | null;
  completed_at?: string | null;
  action_by?: string | null;
  action_at?: string | null;
  action_note?: string | null;
  actioned_by?: {
    worker_id: string;
    name: string;
    email?: string | null;
  } | null;
}

interface FlowNodeLike {
  id: string;
  type: string;
  data?: { label?: string } | null;
}

interface Props {
  flow: { nodes?: FlowNodeLike[]; edges?: unknown[] } | null | undefined;
  tasks: StepTask[];
  currentNodeId?: string | null;
  /** worker_id → display name, resolved by the page. */
  workerNameMap: Map<string, string>;
  formatDate: (value?: string | null) => string;
}

/**
 * Only the steps a PERSON acts on. A start event, a condition gateway and a notification are
 * mechanics, not steps somebody is waiting on — listing them would bury the two rows that matter
 * among five that nobody can act on.
 */
const HUMAN_STEP_TYPES = new Set(['approval', 'task', 'user_task', 'review']);

const InstanceStepList: React.FC<Props> = ({
  flow, tasks, currentNodeId, workerNameMap, formatDate,
}) => {
  const { t } = useTranslation();

  const steps = useMemo(() => {
    const nodes = (flow?.nodes ?? []).filter(
      (n) => HUMAN_STEP_TYPES.has(String(n.type).toLowerCase()),
    );
    const byNode = new Map<string, StepTask>();
    for (const task of tasks) {
      if (task.node_id) byNode.set(task.node_id, task);
    }
    return nodes.map((node, index) => ({
      order: index + 1,
      node,
      task: byNode.get(node.id) ?? null,
      isCurrent: !!currentNodeId && currentNodeId === node.id,
    }));
  }, [flow, tasks, currentNodeId]);

  if (steps.length === 0) return null;

  const nameOf = (id: string) =>
    workerNameMap.get(id) ?? t('processes:instanceDetail.formerWorker');

  return (
    <Stack spacing={0} sx={{ width: '100%' }}>
      {steps.map(({ order, node, task, isCurrent }, i) => {
        const outcome = task?.outcome ?? null;
        const done = task?.status === 'completed' || !!outcome;
        const pending = !!task && !done;

        // Pending/current steps show who can act. Completed/rejected steps show
        // who ACTUALLY acted; eligible candidates remain secondary context.
        const owner = task?.assigned_to ?? null;
        const eligible = (task?.eligible_approver_ids ?? []).filter(Boolean);
        const actorId = task?.actioned_by?.worker_id ?? task?.action_by ?? null;
        const people = done && actorId ? [actorId] : owner ? [owner] : eligible;
        const personName = (id: string) =>
          task?.actioned_by?.worker_id === id
            ? task.actioned_by.name
            : nameOf(id);

        return (
          <Stack
            key={node.id}
            direction="row"
            spacing={1.5}
            alignItems="flex-start"
            sx={{
              py: 1.25,
              // A rule between rows rather than a card each: five bordered boxes stacked is
              // five competing edges (svdy-ui §15).
              borderTop: i === 0 ? 'none' : '1px solid',
              borderColor: 'divider',
            }}
          >
            {/* Order + state, in one column so every row's text starts on one axis. */}
            <Box sx={{ width: 28, textAlign: 'center', flexShrink: 0, pt: 0.25 }}>
              {done && outcome === 'rejected'
                ? <RejectedIcon sx={{ fontSize: 18, color: 'error.main' }} />
                : done
                  ? <DoneIcon sx={{ fontSize: 18, color: 'success.main' }} />
                  : pending
                    ? <PendingIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                    : <UpcomingIcon sx={{ fontSize: 18, color: 'text.disabled' }} />}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {order}. {node.data?.label || node.id}
                </Typography>
                {isCurrent && (
                  <Chip
                    label={t('processes:instanceDetail.stepCurrent')}
                    size="small" variant="outlined" color="warning"
                    sx={{ height: 18, fontSize: '0.65rem', borderRadius: 1 }}
                  />
                )}
                {done && (
                  <Chip
                    label={outcome
                      ? t(`processes:instanceDetail.outcome.${outcome}`, outcome)
                      : t('processes:instanceDetail.stepDone')}
                    size="small" variant="outlined"
                    color={outcome === 'rejected' ? 'error' : 'success'}
                    sx={{ height: 18, fontSize: '0.65rem', borderRadius: 1 }}
                  />
                )}
              </Stack>

              {/* WHO — the reason this list exists. */}
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                {people.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    {task
                      ? t('processes:instanceDetail.stepNobody')
                      : t('processes:instanceDetail.stepNotReached')}
                  </Typography>
                ) : (
                  <>
                    <Typography variant="caption" color="text.secondary">
                      {done && actorId
                        ? t('processes:instanceDetail.stepActionedBy')
                        : owner
                          ? t('processes:instanceDetail.stepWith')
                          : t('processes:instanceDetail.stepAnyOf')}
                    </Typography>
                    {people.slice(0, 4).map((wid) => (
                      <Tooltip key={wid} title={personName(wid)}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Avatar sx={{ width: 20, height: 20, fontSize: '0.6rem', fontWeight: 700 }}>
                            {personName(wid).split(' ').filter(Boolean).slice(0, 2)
                              .map((part: string) => part[0]).join('').toUpperCase() || '?'}
                          </Avatar>
                          <Typography variant="caption">{personName(wid)}</Typography>
                        </Stack>
                      </Tooltip>
                    ))}
                    {people.length > 4 && (
                      <Typography variant="caption" color="text.secondary">
                        +{people.length - 4}
                      </Typography>
                    )}
                  </>
                )}
                {(task?.action_at || task?.completed_at) && (
                  <Typography variant="caption" color="text.disabled">
                    · {formatDate(task.action_at ?? task.completed_at)}
                  </Typography>
                )}
              </Stack>
              {done && actorId && eligible.length > 0 && (
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25 }}>
                  {t('processes:instanceDetail.stepEligible')}: {eligible.map(nameOf).join(', ')}
                </Typography>
              )}
              {task?.action_note && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                  {t('processes:instanceDetail.stepNote')}: {task.action_note}
                </Typography>
              )}
            </Box>
          </Stack>
        );
      })}
    </Stack>
  );
};

export default InstanceStepList;
